import React, { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';

/**
 * The offline bar (issue 312).
 *
 * The issue's requirement is that everything the site cannot do offline gets
 * "an honest 'this needs a connection' rather than a spinner or an error
 * page". One bar says it once, for the whole app, rather than every button
 * learning to say it separately — which is also the only version that stays
 * true as features are added.
 *
 * It says the two things a reader needs: what still works (pages already
 * opened) and what does not (anything that changes something). There is no
 * offline write path at all, deliberately, so the second half is the whole
 * truth rather than a hedge.
 *
 * `navigator.onLine` is famously optimistic — it reports a connection to a
 * network, not to the internet — so this is used only to say something
 * reassuring, never to gate a request. A request that fails while the browser
 * believes it is online still fails the way it always did.
 */
const OfflineNotice = () => {
  const [offline, setOffline] = useState(
    typeof navigator !== 'undefined' && navigator.onLine === false,
  );

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-notice"
      className="w-full px-4 py-2 text-sm flex items-center justify-center gap-2 text-center print:hidden"
      style={{
        background: 'rgba(201,168,76,0.12)',
        borderBottom: '1px solid rgba(201,168,76,0.45)',
        color: 'var(--text-primary)',
      }}
    >
      <WifiOff className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
      <span>
        You are offline. Pages you have already opened are still readable —
        anything that saves or changes something needs a connection.
      </span>
    </div>
  );
};

export default OfflineNotice;
